import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUSES, STATUS_MAP, PRIORITIES, PRIORITY_MAP, statusChipClass, priorityChipClass } from "@/lib/labels";
import ThreatMeter from "@/components/ThreatMeter";
import { Bug, Filter, Plus, ChevronRight, Download, ShieldAlert, Wrench } from "lucide-react";

const SEV_ORDER = { critica: 0, alta: 1, media: 2, baja: 3 };

export default function Dashboard() {
  const { user } = useAuth();
  const [bugs, setBugs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterSev, setFilterSev] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.get("/bugs"), api.get("/stats")]);
      setBugs(b.data);
      setStats(s.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    setExporting(true);
    setError("");
    try {
      const res = await api.get("/export/csv", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match ? match[1] : `bugs_${Date.now()}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Blob error body — try to decode
      const blob = e?.response?.data;
      if (blob && blob.text) {
        try { const txt = await blob.text(); const j = JSON.parse(txt); setError(j.detail || txt); }
        catch { setError(formatApiError(e)); }
      } else setError(formatApiError(e));
    } finally { setExporting(false); }
  };

  const filtered = useMemo(() => {
    return bugs.filter((b) =>
      (filterSev === "all" || b.severity === filterSev) &&
      (filterStatus === "all" || b.status === filterStatus) &&
      (filterPriority === "all" || b.priority === filterPriority)
    );
  }, [bugs, filterSev, filterStatus, filterPriority]);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "admin" || user?.role === "agent";

  const changeStatus = async (id, newStatus, e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    try {
      await api.patch(`/bugs/${id}/status`, { status: newStatus });
      setBugs((s) => s.map((b) => (b.id === id ? { ...b, status: newStatus } : b)));
    } catch (err) { setError(formatApiError(err)); }
  };

  const changePriority = async (id, newPriority, e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    try {
      const { data } = await api.patch(`/bugs/${id}/priority`, { priority: newPriority });
      setBugs((s) => s.map((b) => (b.id === id ? { ...b, priority: newPriority, priority_overridden: data.overridden } : b)));
    } catch (err) { setError(formatApiError(err)); }
  };

  const Stat = ({ label, value, tid }) => (
    <div className="term-panel p-4" data-testid={tid}>
      <div className="text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500 mb-1">{label}</div>
      <div className="text-3xl font-bold text-red-500 tabular-nums">{value}</div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-red-500 uppercase text-xs tracking-[0.4em]">// panel_de_control</p>
          <h1 className="text-4xl md:text-5xl text-neutral-100 uppercase tracking-tighter font-bold mt-2">
            Bugs<span className="text-red-500">.</span>reportados
          </h1>
        </div>
        <Link to="/new" data-testid="dashboard-new-bug-btn" className="term-btn">
          <Plus size={16} /> Reportar bug
        </Link>
      </div>

      {user?.role === "admin" && (
        <div className="mb-6">
          <button
            data-testid="export-csv-btn"
            onClick={exportCsv}
            disabled={exporting}
            className="term-btn-ghost"
            title="Exportar todos los bugs con sus comentarios a CSV"
          >
            <Download size={14} /> {exporting ? "> Generando CSV..." : "> Exportar CSV (admin)"}
          </button>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat tid="stat-total" label="Total" value={stats.total} />
          <Stat tid="stat-brecha" label="Brechas" value={stats.by_status?.brecha_detectada ?? 0} />
          <Stat tid="stat-infiltracion" label="Infiltraciones" value={stats.by_status?.infiltracion_en_curso ?? 0} />
          <Stat tid="stat-parches" label="Parcheados" value={stats.by_status?.parche_aplicado ?? 0} />
        </div>
      )}

      <div className="term-panel p-4 mb-4 flex flex-wrap items-center gap-3">
        <Filter size={14} className="text-red-500" />
        <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">Filtros:</span>
        <select data-testid="filter-severity" value={filterSev} onChange={(e) => setFilterSev(e.target.value)} className="term-input" style={{ width: "auto", padding: "6px 10px" }}>
          <option value="all">Toda severidad</option>
          <option value="critica">Crítica</option>
          <option value="alta">Alta</option>
          <option value="media">Media</option>
          <option value="baja">Baja</option>
        </select>
        <select data-testid="filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="term-input" style={{ width: "auto", padding: "6px 10px" }}>
          <option value="all">Todo estado</option>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.short}</option>)}
        </select>
        <select data-testid="filter-priority" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="term-input" style={{ width: "auto", padding: "6px 10px" }}>
          <option value="all">Toda amenaza</option>
          {PRIORITIES.map((p) => <option key={p.value} value={p.value}>Nivel {p.label}</option>)}
        </select>
        <span className="ml-auto text-xs uppercase tracking-[0.2em] text-neutral-500" data-testid="bugs-count">
          {filtered.length} / {bugs.length}
        </span>
      </div>

      {loading && <div data-testid="dashboard-loading" className="text-red-500 text-sm blink-cursor">Cargando</div>}
      {error && <div data-testid="dashboard-error" className="text-red-500 shake">{error}</div>}

      {!loading && filtered.length === 0 && (
        <div data-testid="dashboard-empty" className="term-panel p-10 text-center">
          <Bug className="mx-auto text-red-500 mb-3" size={32} />
          <div className="text-neutral-300 uppercase tracking-[0.2em] text-sm">No hay bugs registrados</div>
          <div className="text-neutral-500 text-xs mt-2">Reporta el primero para empezar el rastreo.</div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3">
        {filtered
          .slice()
          .sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9))
          .map((b) => (
          <Link key={b.id} to={`/bugs/${b.id}`} data-testid={`bug-row-${b.id}`}
            className="term-panel p-4 flex flex-col md:flex-row md:items-center gap-3 hover:border-red-500 transition-colors">
            <span className={`chip chip-${b.severity}`} data-testid={`bug-sev-${b.id}`}>{b.severity}</span>
            <div className="flex-1 min-w-0">
              <div className="text-neutral-100 truncate text-sm md:text-base font-medium">{b.title}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-[0.15em] mt-1">
                {b.browser === "Otro" && b.browser_other ? `Otro: ${b.browser_other}` : b.browser} · {b.os} · {new Date(b.created_at).toLocaleString()}
              </div>
            </div>

            {/* Priority display / control */}
            {isManager ? (
              <select
                data-testid={`row-priority-select-${b.id}`}
                value={b.priority || "medio"}
                onClick={(e) => e.preventDefault() || e.stopPropagation()}
                onChange={(e) => changePriority(b.id, e.target.value, e)}
                className={`term-input ${priorityChipClass(b.priority)}`}
                style={{ width: "auto", padding: "4px 8px", fontSize: "0.7rem" }}
                title={b.priority_overridden ? "Prioridad manual" : `Sugerida: ${b.priority_suggested || b.priority}`}
              >
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}{p.value === b.priority_suggested ? " (sug)" : ""}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className={priorityChipClass(b.priority)}
                data-testid={`row-priority-${b.id}`}
                title={b.priority_overridden ? "Prioridad ajustada por admin" : "Prioridad sugerida automáticamente"}
              >
                {(PRIORITY_MAP[b.priority]?.label) || (b.priority || "?").toUpperCase()}
              </span>
            )}

            {/* Status display / control */}
            {isManager ? (
              <select
                data-testid={`row-status-select-${b.id}`}
                value={b.status || "brecha_detectada"}
                onClick={(e) => e.preventDefault() || e.stopPropagation()}
                onChange={(e) => changeStatus(b.id, e.target.value, e)}
                className={`term-input ${statusChipClass(b.status)}`}
                style={{ width: "auto", padding: "4px 8px", fontSize: "0.7rem" }}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.short}</option>
                ))}
              </select>
            ) : (
              <span className={statusChipClass(b.status)} data-testid={`row-status-${b.id}`}>
                {(STATUS_MAP[b.status]?.short) || b.status}
              </span>
            )}

            <ChevronRight size={16} className="text-red-500" />
          </Link>
        ))}
      </div>
    </div>
  );
}
